import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

import { Filter } from 'cat-shared/utils'
import { useAuthenticateUser } from 'client/api'
import { getLocalLastUserConfig, startUserSocket, displaySnackBar } from 'client/services'
import { useAppContext, usePageStateContext, usePage } from 'client/hooks'
import { AppButton, AppDivider, AppGroup } from 'client/ui-building-blocks'

import {
  DEFAULT_LANG,
  DEFAULT_THEME,
  FormName,
  AuthenticationFormValues,
} from 'client/utils'

import UsernameInput from './fields/username-input'
import PasswordInput from './fields/password-input'
import AppForm from 'client/ui-building-blocks/AppForm/app-form'

const LoginForm = () => {
  const { t, i18n } = useTranslation()

  const { setAuthUser } = useAppContext()

  const {
    setBrowseQuestionsPageState,
    redirectQuestionId,
    setRedirectQuestionId,
    setAuthForm,
    setRandomizerCategory,
  } = usePageStateContext()

  const { navigateTo } = usePage()

  const { mutateAsync: authenticate, isLoading: isAuthenticating } = useAuthenticateUser({
    authType: 'CREDENTIALS',
    onSuccess: data => {
      const lastUserConfig = getLocalLastUserConfig()
      const html = document.querySelector('html')
      html.setAttribute('data-theme', lastUserConfig?.theme || DEFAULT_THEME)
      i18n.changeLanguage(lastUserConfig?.lang || DEFAULT_LANG)

      if (data.user.config.confirmed) {
        setBrowseQuestionsPageState(state => ({ ...state, filter: Filter.Answered }))
      }

      setAuthUser(data.user)
      startUserSocket(data.user._id.toString())
      setRandomizerCategory('any')

      if (redirectQuestionId) {
        navigateTo({ path: '/question', query: { _id: redirectQuestionId } })
        setRedirectQuestionId(null)
      } else {
        navigateTo({ path: '/home' })
      }
    },
    onError: err => {
      if (err?.msg) {
        displaySnackBar(err.msg)
      }
    },
  })

  const form = useForm<AuthenticationFormValues>({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const {
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = form

  const onClearClick = () => {
    reset()
  }

  const onForgotPasswordClick = () => {
    setAuthForm(FormName.RecoverPasswordForm)
    window.scrollTo(0, 0)
  }

  const onSubmit = handleSubmit(async data => {
    return authenticate({
      credentials: data,
    }).catch(() => null)
  })

  return (
    <>
      <FormProvider {...form}>
        <AppForm onSubmit={onSubmit}>
          <UsernameInput isLoading={isAuthenticating} />
          <PasswordInput isLoading={isAuthenticating} />
          <AppGroup fullWidth="mobile" alignment="verticalMobile">
            <AppButton
              variant="info"
              text={t('common.buttons.login')}
              type="submit"
              loading={isAuthenticating}
              disabled={!isDirty || !isEmpty(errors)}
            />
            <AppButton
              variant="blank"
              text={t('common.buttons.clear')}
              type="reset"
              disabled={isAuthenticating || (!isDirty && isEmpty(errors))}
              onClick={onClearClick}
            />
          </AppGroup>
        </AppForm>
      </FormProvider>
      <AppDivider border size="large" />
      <AppButton
        variant="warning"
        text={t('authPage.login.forgotPassword')}
        disabled={isAuthenticating}
        onClick={onForgotPasswordClick}
        position="center"
      />
    </>
  )
}

export default LoginForm
